/**
 * How to cleanly shut down the media/data path — the mirror image of
 * `connect-phase.ts`. Used both by a hard stop AND by every rejoin (which needs a
 * clean slate before the controller calls `connect-phase.ts` again) — see
 * `operator-session.ts`'s `teardownTransports()`, the only caller.
 *
 * Isolated on purpose: takes exactly the collaborators and the transports to close as
 * arguments, returns nothing, and never calls into `connect-phase.ts` or
 * `recovery-phase.ts` itself — deciding "close, THEN reconnect" is the controller's job.
 */

import type { types as Mediasoup } from "mediasoup-client";

import type { ChatChannel } from "./chat-channel";
import type { ControlChannel } from "./control-channel";
import type { VideoProducerWatchdog } from "./video-producer-watchdog";
import type { ConnectionQualityMonitor } from "../../connection-stats";

/** The one thing this file needs from the room-consumers registry. */
export interface RoomResetter {
  resetForRejoin?: () => void;
}

export interface TeardownPhaseContext {
  room: RoomResetter;
  chat: ChatChannel;
  control: ControlChannel;
  videoWatchdog: VideoProducerWatchdog;
  connectionQuality: ConnectionQualityMonitor;
  recvTransportRef: { current: Mediasoup.Transport | null };
}

export interface PhaseTransports {
  sendTransport: Mediasoup.Transport;
  recvTransport: Mediasoup.Transport;
}

/**
 * @param transports current cycle's transports (the caller discards its own reference
 *   after this resolves)
 */
export async function runTeardownPhase(
  ctx: TeardownPhaseContext,
  transports: PhaseTransports | null
): Promise<void> {
  ctx.videoWatchdog.clear();
  ctx.connectionQuality.stopConnectionQualityMonitor();
  try {
    ctx.room?.resetForRejoin?.();
  } catch {
    /* ignore */
  }
  ctx.chat.close();
  ctx.control.close();
  try {
    transports?.recvTransport?.close();
  } catch {
    /* ignore */
  }
  try {
    transports?.sendTransport?.close();
  } catch {
    /* ignore */
  }
  if (ctx.recvTransportRef) ctx.recvTransportRef.current = null;
}
